import React, { useState, KeyboardEvent, ChangeEvent } from 'react';
import '../../styles/Button.css';

type QuestionChipsInputProps = {
  /** Parent-managed list of sentences/questions */
  questions: string[];
  /** Parent setState to update the list */
  setQuestions: React.Dispatch<React.SetStateAction<string[]>>;
  /** Optional placeholder for the input */
  placeholder?: string;
  /** Optional: prevent duplicates (default true) */
  preventDuplicates?: boolean;
  /** Optional: max number of questions */
  maxItems?: number;
  /** Optional: custom className for container */
  className?: string;
};

const QuestionChipsInput: React.FC<QuestionChipsInputProps> = ({
  questions,
  setQuestions,
  placeholder = 'Type a question and press Enter…',
  preventDuplicates = true,
  maxItems,
  className,
}) => {
  const [value, setValue] = useState('');

  const addQuestion = (text: string) => {
    const cleaned = text.trim();
    if (!cleaned) return;

    if (maxItems && questions.length >= maxItems) return;
    if (preventDuplicates && questions.some(q => q.toLowerCase() === cleaned.toLowerCase())) return;

    setQuestions(prev => [...prev, cleaned]);
    setValue('');
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addQuestion(value);
    }
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
  };

  const handleRemove = (text: string) => {
    setQuestions(prev => prev.filter(q => q !== text));
  };

  return (
    <div
      className={className}
      style={{
        width: '100%',
        // maxWidth: '900px',
        margin: '0 auto',
        padding: '12px 16px',
      }}
    >
      {/* Input at the top */}
      <div
        style={{
          display: 'flex',
          width: '100%',
          backgroundColor: '#fff',
          border: '1px solid #dcdcdc',
          borderRadius: '24px',
          padding: '10px 14px',
          boxShadow: '0px 2px 8px rgba(0,0,0,0.05)',
        }}
      >
        <input
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          aria-label="Add a question"
          style={{
            flex: 1,
            border: 'none',
            outline: 'none',
            fontFamily: 'Montserrat, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif',
            fontSize: '16px',
            lineHeight: '24px',
            color: '#222',
          }}
        />
        <button
          type="button"
          className="responsive-button"
          onClick={() => addQuestion(value)}
          style={{
            border: 'none',
            borderRadius: '18px',
            padding: '8px 12px',
            fontFamily: 'Montserrat',
            fontSize: '14px',
            fontWeight: 600,
            lineHeight: '20px',
            backgroundColor: 'var(--primary)',
            color: '#fff',
            cursor: 'pointer',
            transition: 'transform 0.05s ease',
          }}
          onMouseDown={e => {
            // simple press feedback
            (e.currentTarget as HTMLButtonElement).style.transform = 'scale(0.98)';
          }}
          onMouseUp={e => {
            (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1)';
          }}
        >
          Add
        </button>
      </div>

      {/* Chips / sentences area (your exact style block as base) */}
      <div
        style={{
          position: 'relative',
          zIndex: 15,
          display: 'flex',
          gap: '12px',
          flexWrap: 'wrap',
          justifyContent: 'center',
          marginBottom: '20px',
          top: '30px',
        }}
      >
        {questions.map((question) => (
          <button
            key={question}
            onClick={() => handleRemove(question)}
            className="fade-in-out fade-in"
            aria-label={`Remove question: ${question}`}
            style={{
              fontFamily: 'Montserrat',
              fontSize: '16px',
              backgroundColor: '#f5f5f5',
              border: '1px solid #dcdcdc',
              borderRadius: '20px',
              padding: '10px 16px',
              cursor: 'pointer',
              // optional subtle shadow & hover
              boxShadow: '2px 4px 8px rgba(0, 0, 0, 0.08)',
              transition: 'background-color 0.15s ease, transform 0.05s ease',
            }}
            onMouseDown={e => {
              (e.currentTarget as HTMLButtonElement).style.transform = 'scale(0.98)';
            }}
            onMouseUp={e => {
              (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1)';
            }}
            title="Click to remove"
          >
            {question}
          </button>
        ))}
      </div>
    </div>
  );
};

export default QuestionChipsInput;
